import type { ItemCorrectionsResource, ItemStockStateResource } from '@repo/api-client';

/**
 * Reading the item-level activity report, and the one arithmetic it refuses to
 * do.
 *
 * ## `countedQuantity` is a level, not a flow
 *
 * Every other measure on an item row is a flow: units sold, units handed back,
 * units delivered, the variance the books were adjusted by. Ask for the per-day
 * breakdown and each of those adds up to the range figure beside it, because
 * that is what a flow is.
 *
 * `stock.countedQuantity` does not. It is the **latest** count inside the
 * period — a photograph of the shelf rather than a record of movement — so ten
 * counted on Monday and ten counted on Friday is ten, and never twenty. The
 * resource's own description says so, and this module is written so that the
 * mistake cannot be made downstream: **there is no function here that totals
 * anything**, and the range figure is always read off the item's own row rather
 * than derived from its days.
 *
 * A screen that summed that column would be wrong in a way nobody notices until
 * a stock count is disputed, at which point the screen is the thing being
 * disputed with. `Items.test.tsx` asserts it against a fixture no derivation can
 * satisfy.
 *
 * ## A nil count is not a nought count
 *
 * `applicability` is three-valued and the three are three different facts.
 * `NOT_APPLICABLE` is an outlet with no shelf — a gate sells admissions and
 * there is nothing there to count. `NOT_COUNTED` is a shelf nobody counted.
 * `COUNTED` with `0` is a shelf counted and found empty, which is a reconciled
 * result. Rendering the middle one as `0` — or as a dash, which reads as one —
 * is the lie FR-904 exists to stop, so `readStock` hands the caller a tagged
 * reading rather than a number that has lost which of the three it came from.
 */

/** What a cell says where counting this item here means nothing. */
export const STOCK_NOT_APPLICABLE = 'Not stocked';

/** What a cell says where it would mean something and nobody counted. */
export const STOCK_NOT_COUNTED = 'Not counted';

/**
 * One of the three answers the stock state can carry, with the figure attached
 * to the only one that has one.
 *
 * A tagged union rather than `number | null` on purpose: a caller cannot render
 * this without deciding what to do about the two cases that are not a count,
 * which is exactly the decision the API went three-valued to force.
 */
export type StockReading =
  | { readonly kind: 'NOT_APPLICABLE' }
  | { readonly kind: 'NOT_COUNTED' }
  | { readonly kind: 'COUNTED'; readonly quantity: number };

/**
 * The stock state as one of three readings.
 *
 * `COUNTED` with a null figure is a shape the document says cannot happen —
 * `countedQuantity` is "null unless `COUNTED`", which is not quite the same
 * promise, so it is read as uncounted rather than as nought. The direction of
 * that fallback is the point: inventing a `0` here would publish a reconciled
 * result nobody produced.
 */
export function readStock(stock: ItemStockStateResource): StockReading {
  if (stock.applicability === 'NOT_APPLICABLE') return { kind: 'NOT_APPLICABLE' };

  if (stock.applicability === 'NOT_COUNTED' || stock.countedQuantity === null) {
    return { kind: 'NOT_COUNTED' };
  }

  return { kind: 'COUNTED', quantity: stock.countedQuantity };
}

/**
 * The same three readings as a sentence, for anybody listening rather than
 * looking.
 *
 * The visible cell is terse because it sits in a column of figures. Said aloud,
 * "Not counted" and "Not stocked" are two words apart and both could be heard as
 * a column heading, so this is the long form.
 */
export function describeStock(stock: ItemStockStateResource): string {
  const reading = readStock(stock);

  switch (reading.kind) {
    case 'NOT_APPLICABLE':
      return `${STOCK_NOT_APPLICABLE} — this outlet carries no stock of it, so there is nothing here to count.`;

    case 'NOT_COUNTED':
      return `${STOCK_NOT_COUNTED} — nobody counted this in the period. That is not a count of nought.`;

    case 'COUNTED':
      return `Counted ${formatQuantity(reading.quantity)}, the latest count in the period.`;
  }
}

/** Grouped figures, so a column of them can be read down without counting digits. */
const GROUPING = new Intl.NumberFormat('en-IN', { useGrouping: true });

/** A plain quantity, grouped. */
export function formatQuantity(value: number): string {
  return GROUPING.format(value);
}

/**
 * A quantity whose direction is half of what it means — a variance, and the
 * write-off total beside it.
 *
 * A bare `3` two rows above a `-3` is the thing a person misreads at a glance,
 * so a surplus states its sign as plainly as a shortfall does. Nought states
 * neither: a count that agreed exactly is not a movement in either direction,
 * and `+0` would suggest it was.
 *
 * A true minus sign rather than a hyphen, matching `formatPaise`.
 */
export function formatSignedQuantity(value: number): string {
  if (value === 0) return '0';

  return value < 0 ? `−${formatQuantity(Math.abs(value))}` : `+${formatQuantity(value)}`;
}

/**
 * What came off the shelf other than by selling it, as the movements were
 * stored.
 *
 * Breakage, spoilage and loss are negative and a find is positive, because the
 * write path derives the sign from the reason and reporting publishes what was
 * stored. So this is the level change those four reasons account for, and it is
 * shown with the four beneath it rather than alone — a `−6` with no reasons
 * under it cannot be told from six broken, and six stolen is a different
 * conversation.
 *
 * **`deliveredQuantity` is deliberately not among them.** It is one of the five
 * measures this report keeps apart and it has a column of its own; folding a
 * delivery into a write-off total would net two of the five together, which is
 * the whole thing the read was shaped to avoid.
 */
export function writtenOffQuantity(corrections: ItemCorrectionsResource): number {
  return (
    corrections.breakageQuantity +
    corrections.spoilageQuantity +
    corrections.lossQuantity +
    corrections.foundQuantity
  );
}
